// V8 — Aggressive local SEO authority push for the 3 priority cities:
// Jabalpur · Bhopal · Nagpur.
//
// 9 keyword-targeted pages × 3 cities = 27 pages
// + 2 offline "Near Me" pillar pages (Home Tutor Near Me, Home Tuition Near Me)
// = 29 new authority routes.
//
// Each page is rendered by the existing AuthorityLanding scaffold (so it
// inherits multi-schema EEAT, structured data and analytics tracking) but with
// a city-specific LocalBusiness schema overlay so Google associates the page
// with the city's geo-cluster.
//
// All keyword pages auto-tag with categorizePath() → page_category = the city
// (jabalpur / bhopal / nagpur) because every slug ends with -<city>, so the
// V8 conversions panel shows them inside the priority-city block.

const FOUNDED = "2013";
const STUDENTS = "50,000+";
const RATING = "4.8";
const REVIEWS = "1,200+";

const CITY_META = {
 jabalpur: {
 name: "Jabalpur",
 state: "Madhya Pradesh",
 boards: "CBSE, ICSE, MP Board (MPBSE)",
 local_board: "MP Board",
 phone: "our Jabalpur Head Office team",
 phoneRaw: "our Jabalpur Head Office team",
 head_office: "Ghadi Chowk, Vijay Nagar, Jabalpur, Raksha 482002",
 areas: ["Vijay Nagar", "Civil Lines", "Wright Town", "Napier Town", "Adhartal", "Ranjhi", "Pachpedi", "Madan Mahal", "Gwarighat", "Cantt"],
 landmarks: "DPS, Christ Church, St. Aloysius, KV NCC, IIIT Jabalpur, RDVV, NIT, JEC, GGITS",
 students_count: "8,000+",
 years: "12+",
 intro_proof: "from our flagship Head Office at Ghadi Chowk, Vijay Nagar since 2013",
 },
 bhopal: {
 name: "Bhopal",
 state: "Madhya Pradesh",
 boards: "CBSE, ICSE, MP Board (MPBSE)",
 local_board: "MP Board",
 phone: "our Jabalpur Head Office team",
 phoneRaw: "our Jabalpur Head Office team",
 head_office: "MP Nagar, Bhopal",
 areas: ["MP Nagar", "Arera Colony", "Shahpura", "Kolar Road", "Habibganj", "Bairagarh", "Bittan Market", "Indrapuri", "Govindpura", "Berasia Road"],
 landmarks: "DPS, Sagar Public School, Campion School, Carmel Convent, MANIT, IISER, AIIMS Bhopal, IIFM",
 students_count: "5,000+",
 years: "12+",
 intro_proof: "with active home-tutor deployment across MP Nagar, Arera Colony, Shahpura and 25+ Bhopal localities since 2013",
 },
 nagpur: {
 name: "Nagpur",
 state: "Maharashtra",
 boards: "CBSE, ICSE, Maharashtra Board (MSBSHSE)",
 local_board: "Maharashtra Board",
 phone: "our Jabalpur Head Office team",
 phoneRaw: "our Jabalpur Head Office team",
 head_office: "Dharampeth, Nagpur",
 areas: ["Dharampeth", "Civil Lines", "Ramdaspeth", "Wardha Road", "Manish Nagar", "Khamla", "Sadar", "Bajaj Nagar", "Hingna", "Sitabuldi"],
 landmarks: "Centre Point School, Bhavan's, Modern School, St. Vincent Pallotti, VNIT, AIIMS Nagpur, RTM Nagpur University",
 students_count: "4,000+",
 years: "12+",
 intro_proof: "from our Dharampeth operations center serving Civil Lines, Ramdaspeth, Wardha Road and all of Nagpur since 2013",
 },
};

const _stats = (city) => [
 { v: "1:1", l: "1 tutor : 1 student" },
 { v: city.students_count, l: `${city.name} students taught` },
 { v: "100%", l: "Background-verified tutors" },
 { v: `${RATING}/5`, l: `${REVIEWS} parent reviews` },
];

// ---------------------------------------------------------------------------
// Per-keyword templates. Each takes a CITY_META entry and returns a config.
// ---------------------------------------------------------------------------

function _eeatBlock(city) {
 return [
 { pillar: "Experience", body: `${city.years} years of continuous 1:1 home tuition operations in ${city.name}. ${city.students_count} ${city.name} students taught. Active enrolment from ${city.areas.slice(0, 4).join(", ")} and 20+ other ${city.name} localities. We've never operated as a coaching batch — only true 1:1.` },
 { pillar: "Expertise", body: `${city.name} tutor pool sourced from local reputed institutions — ${city.landmarks}. Subject specialists only: never a generalist on a paying student. ${city.local_board}, CBSE, ICSE, IB, IGCSE specialists matched separately.` },
 { pillar: "Authoritativeness", body: `Published parent and tutor policies on /policies. EducationalOrganization + LocalBusiness + Service + Course + FAQ + Review schemas on every ${city.name} page. Single Customer Care number ${city.phone} (Call / WhatsApp).` },
 { pillar: "Trustworthiness", body: `${RATING}/5 verified rating across ${REVIEWS} parent reviews · transparent process — fees are shared as a personalised quote after the free demo · first-month tutor-replacement window · refund-protected · no cash collection at the door — written into our Tutor Policy.` },
 ];
}

function _coverageBlock(city) {
 return [
 { tag: `${city.name} · Class 1-5`, d: `Primary foundation home tuition — phonics, mental maths, handwriting, language fluency. Tutors visit your ${city.areas[0]} / ${city.areas[1]} home or join live online.` },
 { tag: `${city.name} · Class 6-9`, d: `Algebra and Science foundation, language depth, NTSE / Olympiad layering. ${city.local_board}, CBSE and ICSE specialists.` },
 { tag: `${city.name} · Class 10 Board`, d: `Chapter-weightage mocks, marking-scheme drilling, pre-board cycle Oct-Jan. CBSE, ICSE and ${city.local_board}.` },
 { tag: `${city.name} · Class 11-12 Senior`, d: `PCM / PCB / Commerce / Arts stream specialists. Board + competitive overlap (JEE / NEET / CUET).` },
 { tag: `${city.name} · NEET / JEE`, d: `1:1 AIIMS-alumni NEET mentors · IIT-alumni JEE mentors. Chapter-wise mocks, weekly full-length tests, error journaling.` },
 { tag: `${city.name} · IB / IGCSE`, d: `Cambridge-trained specialists for ${city.name}'s international-curriculum schools.` },
 ];
}

function _localityTagBlock(city) {
 return city.areas.map((a) => ({ name: a, slug: a.toLowerCase().replace(/\s+/g, "-") }));
}

function _faqsCommon(city, focus) {
 return [
 [`How quickly can I get a ${focus} in ${city.name}?`,
 `Most ${city.name} demos are scheduled within 24-48 hours of booking via /contact or WhatsApp ${city.phone}. The first 1:1 demo class is 100% free, no card, no obligation. The tutor either visits your ${city.areas[0]} / ${city.areas[1]} home or joins live online — your choice.`],
 [`Will the same tutor teach my child the entire term?`,
 `Yes — the tutor assigned after the demo continues for the entire term. No 'pool of teachers', no rotating chairs. Continuity is non-negotiable.`],
 [`How much does ${focus.toLowerCase()} cost in ${city.name}?`,
 `Tuition fees depend on multiple factors such as class, subject, board, location, learning goals, and frequency of classes. Contact Anand Tutorials for a personalized recommendation and tutor matching — your free demo class is included.`],
 [`Are background-verified female tutors available in ${city.name}?`,
 `Yes — verified female tutors are available across every subject and every grade in ${city.name}. Police verification, qualification check, demo test and 4-step screening completed before any tutor is matched. Request during booking.`],
 [`Which ${city.name} localities do you cover?`,
 `Active tutor deployment across ${city.areas.join(", ")} and 15+ other ${city.name} pin codes. If your locality isn't listed, call ${city.phone} — chances are we serve it.`],
 [`What boards do you cover for ${city.name} students?`,
 `Full coverage: ${city.boards}, IB, IGCSE, A-Level, AP. Subject-specialist matching per board — never a generalist on a paying student.`],
 ];
}

function _localBusinessSchema(city) {
 // Returned as part of `extraSchemas` so AuthorityLanding can inject it.
 // Embeds geo-coordinates so Google associates the page with the local
 // Google Business Profile cluster.
 const geo = { jabalpur: [23.1815, 79.9864], bhopal: [23.2599, 77.4126], nagpur: [21.1458, 79.0882] }[city.name.toLowerCase()];
 return {
 "@context": "https://schema.org",
 "@type": "LocalBusiness",
 "@id": `https://anandtutorials.com/${city.name.toLowerCase()}#localbusiness`,
 "name": `Anand Tutorials ${city.name}`,
 "telephone": city.phone,
 "url": `https://anandtutorials.com/${city.name.toLowerCase()}`,
 "image": "https://anandtutorials.com/logo-anand-tutorials.png",
 "address": {
 "@type": "PostalAddress",
 "streetAddress": city.head_office,
 "addressLocality": city.name,
 "addressRegion": city.state,
 "addressCountry": "IN",
 },
 "geo": { "@type": "GeoCoordinates", "latitude": geo[0], "longitude": geo[1] },
 "openingHoursSpecification": [{
 "@type": "OpeningHoursSpecification",
 "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
 "opens": "07:00", "closes": "23:00",
 }],
 "areaServed": city.areas.map((a) => ({ "@type": "Place", "name": `${a}, ${city.name}` })),
 "aggregateRating": { "@type": "AggregateRating", "ratingValue": RATING, "reviewCount": REVIEWS.replace(/[^\d]/g, "") || "1200", "bestRating": "5", "worstRating": "1" },
 };
}

// ---------------------------------------------------------------------------
// Page builders — 9 keyword angles
// ---------------------------------------------------------------------------

function _baseShape(city, slug, h1, sub, focusKw, kwList, eyebrowTag) {
 return {
 path: `/${slug}`,
 eyebrow: `🏠 ${eyebrowTag} · ${city.name}, ${city.state}`,
 h1,
 subTitle: sub,
 seoTitle: `${h1} | Anand Tutorials`,
 seoDescription: `${sub.split(".")[0]}. ${RATING}/5 across ${REVIEWS} parent reviews. Free demo with no card. Founded ${FOUNDED}. ${STUDENTS} students taught.`,
 keywords: kwList,
 focusKeyword: focusKw,
 intent: `local-priority-${city.name.toLowerCase()}-${slug}`,
 localBusinessSchema: _localBusinessSchema(city),
 cityLocalities: _localityTagBlock(city),
 stats: _stats(city),
 eeatTitle: `Why ${city.name} parents trust Anand Tutorials for ${focusKw.toLowerCase()}`,
 eeat: _eeatBlock(city),
 coverage: _coverageBlock(city),
 compare: [
 ["Class format", "1:1 home tutor or live two-way HD video", "60-100 student coaching batch"],
 ["Tutor verification", "4-step: qualification + subject test + demo + police check", "Often unverified"],
 ["Same tutor entire term", "✔ Continuity guaranteed", "Rotating pool"],
 ["Sunday parent reports", "✔ WhatsApp written", "Monthly dashboard alert"],
 ["Free demo with no card", "✔", "Often paid mini-class"],
 ["Coverage", `All of ${city.name} — ${city.areas.length}+ localities`, "Limited to one centre"],
 ["Founded", `${FOUNDED} (${city.years} years in ${city.name})`, "Most after 2018"],
 ],
 faqs: _faqsCommon(city, focusKw),
 related: [
 { label: `Home Tuition in ${city.name}`, to: `/${city.name.toLowerCase()}` },
 { label: `1:1 Home Tuition ${city.name}`, to: `/1-to-1-home-tuition-${city.name.toLowerCase()}` },
 { label: `CBSE Home Tutor ${city.name}`, to: `/cbse-home-tutor-${city.name.toLowerCase()}` },
 { label: `Personal Home Tutor ${city.name}`, to: `/personal-home-tutor-${city.name.toLowerCase()}` },
 { label: `Private Tutor Near Me ${city.name}`, to: `/private-tutor-near-me-${city.name.toLowerCase()}` },
 { label: "Home Tutor Near Me", to: "/home-tutor-near-me" },
 { label: "Home Tuition Near Me", to: "/home-tuition-near-me" },
 { label: "Pay Now", to: "/pay-now" },
 { label: "Book a Free Demo", to: "/contact" },
 ],
 };
}

const KW_BUILDERS = [
 // 1 — 1:1 Home Tuition
 (city) => {
 const slug = `1-to-1-home-tuition-${city.name.toLowerCase()}`;
 const p = _baseShape(city, slug,
 `1:1 Home Tuition in ${city.name} — Personal Verified Home Tutors`,
 `Anand Tutorials' 1:1 home tuition in ${city.name} delivers genuine one-on-one teaching — never a batch, never a generalist. Verified ${city.local_board} / CBSE / ICSE / IB tutors visit your home in ${city.areas.slice(0, 4).join(", ")} and 15+ other localities.`,
 `1:1 home tuition ${city.name.toLowerCase()}`,
 `1:1 home tuition ${city.name.toLowerCase()}, one to one home tuition ${city.name.toLowerCase()}, 1 on 1 home tutor ${city.name.toLowerCase()}, personal home tuition ${city.name.toLowerCase()}, ${city.local_board.toLowerCase()} home tuition ${city.name.toLowerCase()}, CBSE 1:1 tuition ${city.name.toLowerCase()}, ICSE home tuition ${city.name.toLowerCase()}, individual home tuition ${city.name.toLowerCase()}`,
 "1:1 Home Tuition");
 p.pillars = [
 { t: "True 1:1 — never a batch", d: `Every Anand Tutorials class in ${city.name} is 1 tutor : 1 student. We have never operated a coaching batch. The lesson is built around your child's pace, gaps and exact ${city.local_board} / CBSE syllabus.` },
 { t: `${city.years} years of 1:1 operations in ${city.name}`, d: `Continuous since ${FOUNDED}. ${city.students_count} ${city.name} students. Same model from day one — no pivot to batch coaching.` },
 { t: "Background-verified tutors only", d: "Every tutor passes 4-step verification: academic credentials, subject test, teaching demo, police background check. We reject 8 of every 10 applicants." },
 { t: `Subject specialists across ${city.boards}`, d: `${city.local_board} specialist Maths tutor, ICSE-specialist Physics tutor, CBSE-specialist Biology tutor — matched by exact subject + board, not 'any teacher available'.` },
 { t: "Weekly Sunday parent reports", d: "Written WhatsApp report every Sunday — what was taught, what was tested, where the gap is, what's next week. Zero ambiguity." },
 { t: `Locality coverage — ${city.areas.slice(0, 6).join(", ")}+`, d: `Active tutor deployment across all major ${city.name} localities. Tutors travel; you don't.` },
 ];
 p.longFormSections = [
 { h2: `Why 1:1 home tuition outperforms coaching batches in ${city.name}`,
 paragraphs: [
 `In a typical ${city.name} coaching batch of 60-100 students, the average per-student interaction is under 60 seconds across a 2-hour class. In Anand Tutorials' 1:1 home tuition in ${city.name}, your child gets the tutor's full undivided attention for 60-90 minutes per session. Subject by subject, that math determines whether marks move. Most coaching strugglers we onboard don't have an effort gap — they have an attention gap that the batch structurally cannot fix.`,
 `1:1 means three concrete things in our ${city.name} operation: (1) diagnostic — the free demo identifies the exact chapter gap, (2) adaptive pacing — if today's session shows your child hasn't internalised yesterday's chapter, we re-teach it differently before moving forward, (3) written accountability — the Sunday WhatsApp report covers what was taught, what was tested, where the gap remains, and the plan for next week. That weekly cadence is the lever ${city.name} parents tell us made the difference.`,
 ] },
 { h2: `${city.local_board}, CBSE and ICSE — board-specific 1:1 tutors in ${city.name}`,
 paragraphs: [
 `Our ${city.name} 1:1 tutor pool is segmented by board, not generic. ${city.local_board} specialists know the chapter weightage, marking scheme and 2-mark / 5-mark question patterns. CBSE specialists work NCERT-first with chapter-aligned mocks. ICSE / ISC specialists drill the council marking scheme chapter by chapter. We never let a CBSE tutor 'try' an ICSE student — board-mismatch is the most common reason coaching outcomes underperform.`,
 `From ${city.areas[0]} to ${city.areas[city.areas.length - 1]}, the same quality bar applies. Demo scheduled within 24-48 hours. Free first session, parents may attend. Tutor continues entire term — no rotation.`,
 ] },
 ];
 return p;
 },

 // 2 — One to One Tuition Classes
 (city) => {
 const slug = `one-to-one-tuition-classes-${city.name.toLowerCase()}`;
 const p = _baseShape(city, slug,
 `One-to-One Tuition Classes in ${city.name} — Personal Verified Tutors`,
 `Anand Tutorials' one-to-one tuition classes in ${city.name} — strictly 1 tutor per student. ${city.boards}, IB, IGCSE. Class 1-12, NEET, JEE. Recorded sessions on request, weekly parent reports.`,
 `one to one tuition classes ${city.name.toLowerCase()}`,
 `one to one tuition classes ${city.name.toLowerCase()}, 1 to 1 tuition ${city.name.toLowerCase()}, one on one tutoring ${city.name.toLowerCase()}, personal tuition classes ${city.name.toLowerCase()}, individual tuition ${city.name.toLowerCase()}, single student tuition ${city.name.toLowerCase()}, private tuition classes ${city.name.toLowerCase()}`,
 "One-to-One Tuition Classes");
 p.pillars = [
 { t: "Strictly 1 tutor : 1 student", d: `Never a batch, never 'small group of 4-5'. Every Anand Tutorials class in ${city.name} is 1:1. Period.` },
 { t: "At-home OR live online — your choice", d: "Tutor visits your home, or joins live two-way HD video — same tutor, same syllabus, your weekly choice based on schedule." },
 { t: "Verified specialist tutors", d: `${city.boards}, IB, IGCSE, NEET, JEE — subject-specialist matching. 4-step verification process.` },
 { t: "Free demo before any payment", d: `100% free 1:1 demo with the matched tutor. No card needed. Most ${city.name} demos scheduled within 24-48 hours.` },
 { t: "Transparent monthly fees", d: "Monthly billing only after you decide to continue. No advance fee." },
 { t: "Weekly parent reports + WhatsApp doubts", d: "Sunday WhatsApp written report every week. Direct doubt-solving with assigned tutor on weekdays for board / competitive students." },
 ];
 return p;
 },

 // 3 — Personal Home Tutor
 (city) => {
 const slug = `personal-home-tutor-${city.name.toLowerCase()}`;
 const p = _baseShape(city, slug,
 `Personal Home Tutor in ${city.name} — Your Child's Dedicated 1:1 Mentor`,
 `A personal home tutor in ${city.name} your child actually looks forward to studying with. Verified, subject-specialist, same tutor entire term, weekly reports. ${city.local_board}, CBSE, ICSE, IB, IGCSE.`,
 `personal home tutor ${city.name.toLowerCase()}`,
 `personal home tutor ${city.name.toLowerCase()}, dedicated home tutor ${city.name.toLowerCase()}, private home tutor ${city.name.toLowerCase()}, personal tutor at home ${city.name.toLowerCase()}, individual home tutor ${city.name.toLowerCase()}, ${city.name.toLowerCase()} personal teacher, my personal tutor ${city.name.toLowerCase()}`,
 "Personal Home Tutor");
 p.pillars = [
 { t: "One tutor your child connects with", d: `Demo first. If chemistry isn't right after 2-3 sessions, we replace at no cost. Per our Parent Policy. ${city.name} parents call this the 'right-fit guarantee'.` },
 { t: "Dedicated for the whole term", d: "No pool, no rotation. Same tutor — exam after exam, term after term. That continuity is the lever." },
 { t: "Reports directly to YOU, the parent", d: "Sunday WhatsApp report. What was taught, what was tested, where the gap is, what's next week." },
 { t: `Knows ${city.name}'s school landscape`, d: `Our ${city.name} tutors know the local curriculum quirks at ${city.landmarks.split(",").slice(0, 4).join(", ")} and other schools. They've taught those exact syllabi.` },
 { t: "Background-verified — non-negotiable", d: "4-step: academic credentials, subject test, teaching demo, police verification. Your home, your child — non-negotiable." },
 { t: "Female tutors available across every subject", d: `Verified female personal home tutors in ${city.name} across every subject and every grade. Police-checked. Request during booking.` },
 ];
 return p;
 },

 // 4 — Private Tutor Near Me
 (city) => {
 const slug = `private-tutor-near-me-${city.name.toLowerCase()}`;
 const p = _baseShape(city, slug,
 `Private Tutor Near Me in ${city.name} — Find a Verified 1:1 Tutor Locally`,
 `Searching 'private tutor near me' from ${city.name}? Anand Tutorials matches you with a verified private home tutor in ${city.areas.slice(0, 3).join(" / ")} and 20+ other ${city.name} pin codes. Free demo, transparent fees.`,
 `private tutor near me ${city.name.toLowerCase()}`,
 `private tutor near me ${city.name.toLowerCase()}, private home tutor near me ${city.name.toLowerCase()}, tutor near me ${city.name.toLowerCase()}, home tutor near me ${city.name.toLowerCase()}, find tutor near me ${city.name.toLowerCase()}, private teacher near me ${city.name.toLowerCase()}, tutor in my area ${city.name.toLowerCase()}, ${city.areas[0].toLowerCase()} home tutor, ${city.areas[1].toLowerCase()} home tutor`,
 "Private Tutor Near Me");
 p.pillars = [
 { t: `Tutors active in every major ${city.name} pin code`, d: `${city.areas.join(", ")} and 15+ other localities. Type your area into our /contact form and we'll surface tutors closest to your home.` },
 { t: "Verified — not 'gig-economy' freelancers", d: "We do NOT operate like classified-listing tutor apps. Every tutor is interviewed, subject-tested, demo-screened and police-verified by us." },
 { t: "Demo before any payment — no card", d: "Free 1:1 demo with the matched tutor at YOUR home (or live online). Most demos within 24-48 hours." },
 { t: "Continuity guarantee — same tutor entire term", d: `Once you pick a tutor after the demo, that tutor is yours for the term. Replacements only if YOU request — no surprise faculty changes.` },
 { t: "Fee transparency — no hidden cost", d: `Monthly billing only. ${city.name} families typically save 30-40% vs the equivalent coaching-batch rate.` },
 { t: "Sunday WhatsApp parent reports", d: "Every parent gets a written Sunday WhatsApp report. Zero ambiguity about what's happening between tutor and child." },
 ];
 return p;
 },

 // 5 — CBSE Home Tutor
 (city) => {
 const slug = `cbse-home-tutor-${city.name.toLowerCase()}`;
 const p = _baseShape(city, slug,
 `CBSE Home Tutor in ${city.name} — 1:1 Verified Tutor for Class 1 to 12`,
 `Anand Tutorials' CBSE home tutors in ${city.name} are NCERT-first, board-pattern-aligned specialists — Class 1 to 12, every subject. Active tutor deployment across ${city.areas.slice(0, 3).join(", ")} and 20+ other localities.`,
 `CBSE home tutor ${city.name.toLowerCase()}`,
 `CBSE home tutor ${city.name.toLowerCase()}, CBSE home tuition ${city.name.toLowerCase()}, CBSE tutor ${city.name.toLowerCase()}, CBSE class 10 tutor ${city.name.toLowerCase()}, CBSE class 12 tutor ${city.name.toLowerCase()}, NCERT home tutor ${city.name.toLowerCase()}, CBSE board home tutor ${city.name.toLowerCase()}, ${city.name.toLowerCase()} CBSE specialist tutor`,
 "CBSE Home Tutor");
 p.pillars = [
 { t: "CBSE specialists — never a generalist", d: `Every CBSE tutor in our ${city.name} pool has minimum 3 years of teaching the exact CBSE syllabus. NCERT chapter-by-chapter, sample papers refreshed each November.` },
 { t: "Board-pattern drilling — Class 10 & 12 focus", d: "Pre-board cycle from October to January. 2-mark / 3-mark / 5-mark question typing. Marking-scheme alignment. CBSE board examiners in pool." },
 { t: `${city.name} CBSE schools — we know the syllabus`, d: `Our tutors have taught students from ${city.landmarks.split(",").slice(0, 5).join(", ")} and other ${city.name} CBSE schools. Local exam-pattern fluency.` },
 { t: "Every subject — Class 1-12", d: "Maths, Physics, Chemistry, Biology, English, Hindi, Sanskrit, Social Studies, Computer Science, Accountancy, Business Studies, Economics — full coverage." },
 { t: "1:1 at home OR live online", d: "Tutor visits your home, or joins live two-way HD video. Same tutor, your weekly choice." },
 { t: "Free CBSE demo class — no card", d: `100% free 1:1 demo with the matched CBSE tutor. Most ${city.name} CBSE demos scheduled within 24-48 hours.` },
 ];
 return p;
 },

 // 6 — ICSE Home Tutor
 (city) => {
 const slug = `icse-home-tutor-${city.name.toLowerCase()}`;
 const p = _baseShape(city, slug,
 `ICSE Home Tutor in ${city.name} — 1:1 ISC & ICSE Specialists`,
 `Anand Tutorials' ICSE home tutors in ${city.name} are council-marking-scheme specialists. Descriptive answer-writing drilled chapter by chapter for Class 1 to 12 ICSE / ISC. Free demo, no card.`,
 `ICSE home tutor ${city.name.toLowerCase()}`,
 `ICSE home tutor ${city.name.toLowerCase()}, ICSE home tuition ${city.name.toLowerCase()}, ICSE tutor ${city.name.toLowerCase()}, ISC tutor ${city.name.toLowerCase()}, ICSE class 10 tutor ${city.name.toLowerCase()}, ISC class 12 tutor ${city.name.toLowerCase()}, ${city.name.toLowerCase()} ICSE specialist tutor, council marking scheme tutor ${city.name.toLowerCase()}`,
 "ICSE / ISC Home Tutor");
 p.pillars = [
 { t: "Council marking-scheme specialists", d: `ICSE / ISC tutors in ${city.name} are drilled in the council marking scheme. Descriptive answer-writing, not just objective answers — chapter by chapter.` },
 { t: "Class 10 ICSE + Class 12 ISC focus", d: "Pre-board cycle from October. Council past papers, examiner reports, answer-writing structure week by week." },
 { t: `${city.name} ICSE school knowledge`, d: `Tutors familiar with the local ICSE school landscape — typical English Language paper expectations, History/Civics formats, council Maths-2 patterns.` },
 { t: "Every ICSE / ISC subject", d: "English Language & Literature, Maths, Physics, Chemistry, Biology, History/Civics, Geography, Computer Applications, Economics, Commerce, Accounts." },
 { t: "1:1 at home OR live online", d: "Tutor visits your home in your locality, or live two-way HD video on Zoom — your weekly choice." },
 { t: "Free ICSE demo — no card", d: `100% free 1:1 ICSE demo. Most ${city.name} ICSE demos scheduled within 24-48 hours via ${city.phone}.` },
 ];
 return p;
 },

 // 7 — State Board Home Tutor
 (city) => {
 const slug = `state-board-home-tutor-${city.name.toLowerCase()}`;
 const p = _baseShape(city, slug,
 `${city.local_board} Home Tutor in ${city.name} — State Board Specialists`,
 `Anand Tutorials' ${city.local_board} home tutors in ${city.name} are syllabus-fluent specialists who know the exact ${city.local_board} chapter weightage and exam-pattern. Class 1 to 12, every subject.`,
 `${city.local_board.toLowerCase()} home tutor ${city.name.toLowerCase()}`,
 `${city.local_board.toLowerCase()} home tutor ${city.name.toLowerCase()}, state board home tutor ${city.name.toLowerCase()}, state board tuition ${city.name.toLowerCase()}, ${city.local_board.toLowerCase()} tuition ${city.name.toLowerCase()}, ${city.local_board.toLowerCase()} class 10 tutor ${city.name.toLowerCase()}, ${city.local_board.toLowerCase()} class 12 tutor ${city.name.toLowerCase()}, ${city.name.toLowerCase()} state board specialist tutor`,
 `${city.local_board} Home Tutor`);
 p.pillars = [
 { t: `${city.local_board} syllabus fluency`, d: `Every state-board tutor in ${city.name} knows the ${city.local_board} chapter weightage and marking pattern. Local textbook-aligned, not generic NCERT.` },
 { t: `${city.local_board} — English-medium tuition`, d: `Anand Tutorials operates English-medium only for ${city.local_board} students. Local language may be used briefly in class as a supportive aid when the child needs extra clarification.` },
 { t: "Class 10 & 12 board-year focus", d: "Pre-board cycle, chapter-weightage mocks, marking-scheme drilling. Real outcomes — 98% pass rate." },
 { t: `${city.name} state board schools — local knowledge`, d: `Our tutors have worked with students from ${city.name}'s major ${city.local_board} schools. Local exam-pattern fluency.` },
 { t: "Every state-board subject", d: "Hindi, English, Maths, Science, Social Science, Sanskrit, Computer Science — Class 1 to 12, full coverage." },
 { t: "Free state-board demo — no card", d: `100% free 1:1 demo with the matched ${city.local_board} tutor. Most demos scheduled within 24-48 hours.` },
 ];
 return p;
 },

 // 8 — NEET Home Tutor
 (city) => {
 const slug = `neet-home-tutor-${city.name.toLowerCase()}`;
 const p = _baseShape(city, slug,
 `NEET Home Tutor in ${city.name} — 1:1 AIIMS-Alumni Mentors`,
 `Anand Tutorials' NEET home tutors in ${city.name} are AIIMS-scorer mentors — chapter-wise mocks, weekly full-length tests, error journaling, exam-day strategy. 1:1 mentorship that produces results.`,
 `NEET home tutor ${city.name.toLowerCase()}`,
 `NEET home tutor ${city.name.toLowerCase()}, NEET tutor ${city.name.toLowerCase()}, NEET coaching at home ${city.name.toLowerCase()}, NEET preparation ${city.name.toLowerCase()}, NEET biology tutor ${city.name.toLowerCase()}, NEET physics tutor ${city.name.toLowerCase()}, NEET chemistry tutor ${city.name.toLowerCase()}, AIIMS coaching ${city.name.toLowerCase()}, medical entrance tutor ${city.name.toLowerCase()}`,
 "NEET Home Tutor");
 p.pillars = [
 { t: "AIIMS-scorer mentors", d: `Our ${city.name} NEET mentors are personally AIIMS or top-medical-college scorers — not 'NEET coaching faculty'. Their score is your child's blueprint.` },
 { t: "1:1 — not 80-student NEET batch", d: `NEET in a 100-kid batch is structurally weaker than 1:1 mentorship. Every Anand Tutorials NEET student in ${city.name} gets dedicated 1:1 sessions.` },
 { t: "Chapter-wise mocks + full-length tests", d: "Weekly chapter-wise mocks, monthly full-length papers, error journaling, percentile drift tracking, exam-day strategy." },
 { t: "Bio / Chem / Phy specialists separately", d: "Biology mentor for Biology classes, Chemistry mentor for Chem, Physics mentor for Phy. Subject-specialist 1:1 — never a generalist." },
 { t: "WhatsApp doubt-solving weekdays", d: "Direct WhatsApp doubt-solving with assigned NEET mentor on weekdays. No queue, no 'doubt counter'." },
 { t: "Free NEET demo — no card", d: `100% free 1:1 NEET demo with the matched mentor. Schedule via ${city.phone}.` },
 ];
 return p;
 },

 // 9 — JEE Home Tutor
 (city) => {
 const slug = `jee-home-tutor-${city.name.toLowerCase()}`;
 const p = _baseShape(city, slug,
 `JEE Home Tutor in ${city.name} — 1:1 IIT-Alumni Mentors`,
 `Anand Tutorials' JEE home tutors in ${city.name} are IIT-alumni mentors — 1:1 mentorship, chapter-wise mocks, weekly full-length tests, error journaling. Engineering entrance the right way.`,
 `JEE home tutor ${city.name.toLowerCase()}`,
 `JEE home tutor ${city.name.toLowerCase()}, JEE tutor ${city.name.toLowerCase()}, IIT JEE coaching at home ${city.name.toLowerCase()}, JEE preparation ${city.name.toLowerCase()}, JEE main tutor ${city.name.toLowerCase()}, JEE advanced tutor ${city.name.toLowerCase()}, IIT coaching ${city.name.toLowerCase()}, engineering entrance tutor ${city.name.toLowerCase()}, JEE physics tutor ${city.name.toLowerCase()}, JEE maths tutor ${city.name.toLowerCase()}`,
 "JEE Home Tutor");
 p.pillars = [
 { t: "IIT-alumni mentors", d: `Our ${city.name} JEE mentors are personally IIT-Delhi / IIT-Bombay / IIT-Madras alumni — not 'JEE coaching faculty'. Their JEE journey is your child's roadmap.` },
 { t: "1:1 — not 100-student batch", d: `JEE in a 100-kid batch is structurally weaker. Every Anand Tutorials JEE student in ${city.name} gets dedicated 1:1 mentorship sessions at home or live online.` },
 { t: "Maths / Physics / Chem specialists", d: "Subject-specialist mentors — Maths mentor for Maths, Physics for Phy, Chem for Chem. Never a generalist." },
 { t: "Chapter-wise mocks + full-length", d: "Weekly chapter-wise mocks, monthly full-length papers, advanced-level problem sets, error journaling, exam-day strategy." },
 { t: "WhatsApp doubts weekdays", d: "Direct WhatsApp doubt-solving with assigned JEE mentor on weekdays. No queue." },
 { t: "Free JEE demo — no card", d: `100% free 1:1 JEE demo with the matched IIT mentor. Schedule via ${city.phone}.` },
 ];
 return p;
 },
];

// ---------------------------------------------------------------------------
// 2 OFFLINE Near-Me pillars (Home Tutor Near Me + Home Tuition Near Me)
// ---------------------------------------------------------------------------

function _offlineNearMeBase(slug, h1, sub, focus, kw) {
 return {
 path: `/${slug}`,
 eyebrow: "🏠 Verified Home Tutors · Across India",
 h1,
 subTitle: sub,
 seoTitle: `${h1} | Anand Tutorials`,
 seoDescription: `${sub.split(".")[0]}. ${RATING}/5 across ${REVIEWS} parent reviews. Free demo, no card. Founded ${FOUNDED}. ${STUDENTS} students taught.`,
 keywords: kw,
 focusKeyword: focus,
 intent: "near-me-offline-pillar",
 enableGeoBlock: true,
 stats: [
 { v: "1:1", l: "1 tutor : 1 student" },
 { v: "2,500+", l: "Verified Indian tutors" },
 { v: "12+", l: "Years of operations" },
 { v: `${RATING}/5`, l: `${REVIEWS} parent reviews` },
 ],
 pillars: [
 { t: "Background-verified home tutors only", d: "Every Anand Tutorials home tutor passes 4-step verification: academic credentials, subject test, teaching demo, police background check. We reject 8 of every 10 applicants. We are not a classified-tutor app — we curate." },
 { t: "Subject + board specialists, never generalists", d: "We never let a CBSE tutor 'try' an ICSE student. We never put a Maths specialist on an English request. Subject + board match — non-negotiable." },
 { t: "1:1 means 1 tutor : 1 student — every single class", d: "Never a batch, never 'small group of 4-5'. Same tutor, same kid, same time slot, every class." },
 { t: "Free demo before any payment — no card", d: "100% free 1:1 demo with the matched home tutor at your home (or live online). Most demos scheduled within 24-48 hours of booking." },
 { t: "Continuity guarantee — same tutor entire term", d: "Once you choose a tutor after the demo, that tutor is yours for the full term. Replacements only if YOU request. No surprise faculty changes." },
 { t: "Weekly Sunday parent reports + WhatsApp doubts", d: "Sunday WhatsApp report every week — what was taught, what was tested, where the gap is, what's next week. Plus direct WhatsApp doubt-solving with assigned tutor on weekdays for board / competitive students." },
 ],
 eeatTitle: "Why Indian parents trust Anand Tutorials for home tuition",
 eeat: [
 { pillar: "Experience", body: `Continuous 1:1 home tuition + online tuition since ${FOUNDED}. ${STUDENTS} students taught. Active in Jabalpur, Bhopal, Nagpur — and Pan-India online for every other Indian city.` },
 { pillar: "Expertise", body: "Subject specialists only — Maths specialist for Maths, Physics specialist for Physics, NEET mentor with AIIMS score, JEE mentor with IIT degree. Documented minimum 3 years of teaching the exact curriculum your child follows." },
 { pillar: "Authoritativeness", body: "Published parent and tutor policies on /policies. EducationalOrganization + LocalBusiness + Course + FAQ + Review schemas on every authority page. Single Customer Care number our Jabalpur Head Office team (Call / WhatsApp)." },
 { pillar: "Trustworthiness", body: `${RATING}/5 verified rating across ${REVIEWS} parent reviews · transparent process — fees are shared as a personalised quote after the free demo · first-month tutor-replacement window · no cash collection at the door — written into Tutor Policy.` },
 ],
 coverage: [
 { tag: "Jabalpur · Home Tuition", d: "8,000+ Jabalpur students. Active in Vijay Nagar, Civil Lines, Wright Town, Adhartal, Ranjhi, Pachpedi and 20+ other localities. Head Office: Ghadi Chowk, Vijay Nagar." },
 { tag: "Bhopal · Home Tuition", d: "5,000+ Bhopal students. Active in MP Nagar, Arera Colony, Shahpura, Kolar Road, Habibganj, Bairagarh and 25+ other localities." },
 { tag: "Nagpur · Home Tuition", d: "4,000+ Nagpur students. Active in Dharampeth, Civil Lines, Ramdaspeth, Wardha Road, Manish Nagar, Khamla and 20+ other localities." },
 { tag: "Pan-India · 1:1 Online Tuition", d: "Every Indian city, town and pin code. Tutor sits anywhere in India; class is in your child's room via Zoom or Google Meet. 25+ international countries served too." },
 { tag: "All Boards", d: "CBSE, ICSE, ISC, all State Boards (MP / Maharashtra / UP / TN / KA / etc.), IB MYP & DP, IGCSE Cambridge, AS / A-Level, AP." },
 { tag: "All Grades", d: "Nursery / KG to Class 12, plus undergraduate engineering, NEET / JEE / CUET / SAT / AP, IELTS / TOEFL, spoken English, computer courses." },
 ],
 compare: [
 ["Class format", "1:1 home tutor (or live online)", "60-100 student coaching batch"],
 ["Tutor verification", "4-step + police check", "Often unverified classifieds"],
 ["Same tutor entire term", "✔", "Rotating pool"],
 ["Sunday parent reports", "✔ WhatsApp written", "Monthly dashboard alert"],
 ["Free demo with no card", "✔", "Often paid mini-class"],
 ["Founded", `${FOUNDED}`, "Most after 2018"],
 ],
 faqs: [
 ["How quickly can a home tutor reach me?",
 "Most demos are scheduled within 24-48 hours of booking via /contact or WhatsApp our Jabalpur Head Office team. For Jabalpur, Bhopal and Nagpur, the tutor visits your home directly. For any other Indian city, the 1:1 live online session is set up on Zoom or Google Meet."],
 ["Are background-verified female home tutors available?",
 "Yes — verified female tutors are available across every subject and every grade. Police-verified, qualification-checked, demo-tested. Request during booking."],
 ["What's the cost of 1:1 home tuition?",
 "Tuition fees depend on multiple factors such as class, subject, board, location, learning goals, and frequency of classes. Contact Anand Tutorials for a personalized recommendation and tutor matching — your free demo class is included."],
 ["Can I take a free demo before paying?",
 "Yes — your first 1:1 demo class is 100% free, no card, no obligation."],
 ["Will the same tutor teach the entire term?",
 "Yes — continuity guaranteed. Replacement only if YOU request."],
 ["Which boards do you cover?",
 "Full coverage: CBSE, ICSE, ISC, all State Boards, IB, IGCSE, A-Level, AP."],
 ],
 related: [
 { label: "Home Tuition in Jabalpur", to: "/jabalpur" },
 { label: "Home Tuition in Bhopal", to: "/bhopal" },
 { label: "Home Tuition in Nagpur", to: "/nagpur" },
 { label: "1:1 Home Tuition Jabalpur", to: "/1-to-1-home-tuition-jabalpur" },
 { label: "1:1 Home Tuition Bhopal", to: "/1-to-1-home-tuition-bhopal" },
 { label: "1:1 Home Tuition Nagpur", to: "/1-to-1-home-tuition-nagpur" },
 { label: "Online Tuition Near Me", to: "/online-tuition-near-me" },
 { label: "Best Home Tuition Provider in India", to: "/best-home-tuition-provider-in-india" },
 { label: "Pay Now", to: "/pay-now" },
 { label: "Book a Free Demo", to: "/contact" },
 ],
 longFormSections: [
 { h2: "Why 1:1 home tuition outperforms coaching batches",
 paragraphs: [
 `In a coaching batch of 60-100 students, the average per-student direct interaction is under 60 seconds across a 2-hour class. In 1:1 home tuition, your child has the tutor's full attention for 60-90 minutes per session. Subject by subject, that math determines whether marks move. Most coaching strugglers we onboard don't have an effort gap — they have an attention gap that the batch structurally cannot fix.`,
 `1:1 personalised learning means three concrete things: (1) diagnostic — the free demo identifies the exact chapter gap, (2) adaptive pacing — if today's session shows your child hasn't internalised yesterday's chapter, we re-teach it differently before moving forward, (3) written accountability — Sunday WhatsApp report covers what was taught, what was tested, where the gap remains, and the plan for next week.`,
 ] },
 { h2: "Verified vs unverified tutors — why curation matters",
 paragraphs: [
 `Classified-listing tutor apps onboard whoever signs up. We are deliberately the opposite. Every Anand Tutorials home tutor passes 4-step verification: (1) academic credentials check, (2) subject test in the tutor's claimed specialism, (3) teaching demo with a senior member of our team, (4) police background check. We reject 8 of every 10 applicants. The result is a roster of subject specialists — not generalists trying out tutoring as a side gig.`,
 `For parents, this matters more than the rate. A low-priced unverified tutor who doesn't know the marking scheme is more expensive than a slightly higher-priced verified specialist who lifts board outcomes by 15-20 percentage points. We've seen this play out 50,000+ times since ${FOUNDED}.`,
 ] },
 ],
 };
}

const NEAR_ME_OFFLINE = {
 "home-tutor-near-me": _offlineNearMeBase(
 "home-tutor-near-me",
 "Home Tutor Near Me — Verified 1:1 Home Tutors Across India",
 "Searching 'home tutor near me'? Anand Tutorials matches your child with a verified 1:1 home tutor — background-checked, subject specialist, same tutor entire term. Jabalpur, Bhopal, Nagpur direct home visits. Pan-India 1:1 live online for every other city.",
 "home tutor near me",
 "home tutor near me, home tutor in my area, home tutor in my locality, find home tutor near me, private home tutor near me, verified home tutor near me, best home tutor near me, home tutor for class 10 near me, home tutor for class 12 near me, home tutor for CBSE near me, home tutor for ICSE near me",
 ),
 "home-tuition-near-me": _offlineNearMeBase(
 "home-tuition-near-me",
 "Home Tuition Near Me — 1:1 Verified Tuition at Your Home",
 "Searching 'home tuition near me'? Anand Tutorials delivers 1:1 home tuition at your home in Jabalpur, Bhopal, Nagpur — plus Pan-India 1:1 live online for every other Indian city. Verified subject-specialist tutors, weekly parent reports.",
 "home tuition near me",
 "home tuition near me, home tuition in my area, 1:1 home tuition near me, private home tuition near me, best home tuition near me, home tuition in my locality, home tuition CBSE near me, home tuition ICSE near me, home tuition class 10 near me, home tuition class 12 near me, home tuition for NEET near me, home tuition for JEE near me",
 ),
};

// ---------------------------------------------------------------------------
// Build + export
// ---------------------------------------------------------------------------

const _cityPages = {};
for (const cityKey of Object.keys(CITY_META)) {
 const c = CITY_META[cityKey];
 for (const builder of KW_BUILDERS) {
 const p = builder(c);
 _cityPages[p.path.slice(1)] = p;
 }
}

export const V8_PRIORITY_CITY_PAGES = {..._cityPages,...NEAR_ME_OFFLINE,
};

// Routes that should auto-emit the Near Me geo block in AuthorityLanding.
export const V8_NEAR_ME_OFFLINE_SLUGS = Object.keys(NEAR_ME_OFFLINE);

// All V8 paths — used by backend sitemap + SEO defaults seeder.
export const V8_ALL_PATHS = Object.keys(V8_PRIORITY_CITY_PAGES).map((s) => `/${s}`);
